import ContractValidator from './contractValidator';
import { extractTextFromPDF, isPDFFile } from './pdfTextExtractor';
import { sendToGemini } from './geminiClient';

/**
 * Generate client email content based on contract data
 * @param {Object} contractData - Parsed contract information
 * @param {Object} options - Processing options including client type
 * @returns {string} Formatted email content
 */
function generateClientEmail(contractData, options = {}) {
  const clientType = options.clientType || 'buyer'; // Default to buyer
  const clientName = getClientName(contractData, clientType);
  
  // Determine if we're representing buyer or seller
  const isBuyerAgent = clientType === 'buyer';
  const actionWord = isBuyerAgent ? 'purchase' : 'sale';
  const propertyRef = contractData.address || 'the property';
  
  let email = `Congratulations, ${clientName}!\n\n`;
  email += `Your ${actionWord} agreement for ${propertyRef} is now fully executed. Here are the important details and upcoming deadlines:\n\n`;
  
  // Add deposit information if available
  if (contractData.deposits) {
    email += `DEPOSIT SCHEDULE:\n`;
    email += `${contractData.deposits}\n\n`;
  }
  
  // Add key deadlines
  email += `IMPORTANT DEADLINES:\n`;
  
  if (contractData.inspection_deadline) {
    email += `• Inspection Deadline: ${formatDate(contractData.inspection_deadline)} - [Add to Calendar]\n`;
  }
  
  if (contractData.financing_deadline) {
    email += `• Financing Deadline: ${formatDate(contractData.financing_deadline)} - [Add to Calendar]\n`;
  }
  
  if (contractData.attorney_review_deadline) {
    email += `• Attorney Review Deadline: ${formatDate(contractData.attorney_review_deadline)} - [Add to Calendar]\n`;
  }
  
  if (contractData.closing_date) {
    email += `• Closing Date: ${formatDate(contractData.closing_date)} - [Add to Calendar]\n`;
  }
  
  // Add other important terms
  if (contractData.other_important_terms && contractData.other_important_terms.length > 0) {
    email += `\nADDITIONAL IMPORTANT TERMS:\n`;
    contractData.other_important_terms.forEach(term => {
      if (term && term.trim()) {
        email += `• ${term}\n`;
      }
    });
  }
  
  // Add custom message if provided
  if (options.customMessage && options.customMessage.trim()) {
    email += `\n${options.customMessage}\n`;
  }
  
  email += `\nI'll be monitoring all deadlines and will reach out as each approaches. Please don't hesitate to call with any questions!`;
  
  return email;
}

/**
 * Extract client name based on whether we represent buyer or seller
 */
function getClientName(contractData, clientType) {
  const names = clientType === 'buyer' ? contractData.buyer : contractData.seller;
  
  if (!names) return 'Client';
  
  // Extract first names for friendly greeting
  // "John Smith and Jane Smith" → "John & Jane"
  // "Cameron Scully and Kyla Maclean" → "Cameron & Kyla"
  const nameMatch = names.match(/^([A-Za-z]+).*?\s+and\s+([A-Za-z]+)/);
  if (nameMatch) {
    return `${nameMatch[1]} & ${nameMatch[2]}`;
  }
  
  // Single name: "John Smith" → "John"
  const singleMatch = names.match(/^([A-Za-z]+)/);
  if (singleMatch) {
    return singleMatch[1];
  }
  
  return names; // Fallback to full names
}

/**
 * Format date for email display
 */
function formatDate(dateString) {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  } catch (error) {
    return dateString; // Return original if parsing fails
  }
}

/**
 * Boilerplate terms to filter out from other_important_terms
 * Focus on unusual, atypical terms instead of standard addendum language
 */
const boilerplateTerms = [
  /municipal water/i,
  /municipal sewer/i,
  /digital signatures/i,
  /vacant and broom clean/i,
  /no pending lawsuits/i,
  /not a short sale/i,
  /no special assessments/i,
  /no known betterments/i,
  /no agency violations/i,
  /not a foreign person/i,
  /assign warranties/i,
  /deliver keys/i,
  /represent lender/i,
  /deed personally/i,
  /email transmissions/i,
  /binding signatures/i,
  /time is of the essence/i,
  /execute documents/i,
  /good marketable title/i,
  /free and clear/i,
  /customary closing costs/i,
  /registry of deeds/i,
  /possession at closing/i,
  /smoke detectors/i,
  /carbon monoxide/i,
  /lead paint disclosure/i,
  /title insurance/i,
  /homeowner.?s insurance/i,
  /property insurance/i,
  /utilities/i,
  /oil tank/i,
  /septic system/i,
  /well water/i,
  /radon testing/i,
  /pest inspection/i,
  /home inspection/i,
  /as-is condition/i,
  /reasonable condition/i,
  /normal wear and tear/i
];

/**
 * Filter out boilerplate terms to focus on unusual/custom terms
 * @param {string} text - Term text to check
 * @returns {boolean} - True if term is custom/unusual, false if boilerplate
 */
function isCustomTerm(text) {
  if (!text || typeof text !== 'string') return false;
  return !boilerplateTerms.some(pattern => pattern.test(text.trim()));
}

/**
 * Intelligently process addenda-related terms
 * @param {Array} terms - Array of extracted terms
 * @param {string} contractText - Full contract text for addenda detection
 * @returns {Array} - Processed terms with addenda handling
 */
function processAddendaTerms(terms, contractText) {
  if (!Array.isArray(terms)) return terms;
  
  // Check if document contains addenda
  const hasAddenda = /\b(addendum|buyer addendum|seller addendum)\b/i.test(contractText);
  
  if (!hasAddenda) {
    console.log('📄 No addenda detected, processing terms normally');
    return terms;
  }
  
  console.log('📄 Addenda detected, applying intelligent processing');
  
  // Patterns for actionable terms we should extract from addenda
  const actionablePatterns = [
    // Dollar amounts affecting closing/credits/repairs
    /\$[\d,]+.*(?:closing cost|credit|repair|allowance)/i,
    /(?:closing cost|credit|repair|allowance).*\$[\d,]+/i,
    
    // Clear contingencies
    /contingent\s+upon\s+sale\s+of/i,
    /subject\s+to.*selling/i,
    
    // Special occupancy arrangements
    /(?:seller|buyer).*(?:rent-back|early occupancy|post-closing possession)/i,
    /rent.*back/i,
    /occupy.*before.*closing/i,
    
    // Unusual financing
    /(?:seller|owner).*financing/i,
    /private.*loan/i,
    
    // Specific deadlines with dollar consequences
    /\$[\d,]+.*(?:penalty|forfeit|additional)/i
  ];
  
  // Filter terms: keep actionable ones, replace others with addenda reference
  const processedTerms = [];
  let hasActionableTerms = false;
  let hasNonActionableAddendaTerms = false;
  
  terms.forEach(term => {
    if (!term || typeof term !== 'string') return;
    
    const isActionable = actionablePatterns.some(pattern => pattern.test(term));
    
    if (isActionable) {
      processedTerms.push(term);
      hasActionableTerms = true;
      console.log('✅ Keeping actionable addenda term:', term);
    } else {
      // Check if this looks like an addenda-related term
      const isAddendaRelated = /\b(addendum|exhibit|attachment|schedule)\b/i.test(term) ||
                               term.length > 100; // Long complex terms likely from addenda
      
      if (isAddendaRelated) {
        hasNonActionableAddendaTerms = true;
        console.log('🔄 Replacing addenda term with reference:', term.substring(0, 50) + '...');
      } else {
        // Keep non-addenda terms
        processedTerms.push(term);
        console.log('✅ Keeping non-addenda term:', term);
      }
    }
  });
  
  // Add addenda reference if we had non-actionable addenda terms
  if (hasNonActionableAddendaTerms) {
    processedTerms.push('See attached addendum(s)');
    console.log('📎 Added addenda reference');
  }
  
  console.log(`📊 Addenda processing complete: ${terms.length} → ${processedTerms.length} terms`);
  
  return processedTerms;
}

/**
 * Processes a contract file using Gemini API with text extraction
 * @param {File} file - The contract PDF file
 * @returns {Promise<Object>} Parsed contract data with success/error status
 */
export async function processContractWithGemini(file) {
  try {
    console.log("🔥 PROCESSING START - File:", file.name, "Size:", file.size);
    
    if (!isPDFFile(file)) {
      throw new Error("Only PDF files are supported");
    }

    console.log("🔄 Extracting text from PDF...");
    const contractText = await extractTextFromPDF(file);
    
    // ✅ More lenient text validation (your PDF had 182 chars and worked!)
    if (!contractText || contractText.trim().length < 50) {
      throw new Error(`Could not extract sufficient text from PDF. File may be scanned or corrupted.

Having trouble? Convert your scanned document to searchable text:
👉 Click here: https://tools.pdf24.org/en/ocr-pdf

This tool will convert image-based PDFs to searchable text that our system can process.`);
    }

    console.log("📄 Extracted text length:", contractText.length);
    console.log("📄 First 500 characters:", contractText.substring(0, 500));
    console.log("📄 Searching for buyer/seller keywords...");
    const buyerMatch = contractText.match(/buyer[s]?[:\s]+([^\n,]+)/i);
    const sellerMatch = contractText.match(/seller[s]?[:\s]+([^\n,]+)/i);
    console.log("📄 Found 'buyer' context:", buyerMatch ? buyerMatch[0] : 'not found');
    console.log("📄 Found 'seller' context:", sellerMatch ? sellerMatch[0] : 'not found');

    const prompt = `
Review this real estate contract and extract all dates, deadlines, and key information relevant to buyers, sellers, and agents for calendar tracking.

CRITICAL: ONLY extract information that is EXPLICITLY stated in the contract. Do NOT infer, assume, or create dates that are not present.

Extract the following information and return ONLY a JSON object in this format:

{
  "buyer": "Full legal name(s) - look for 'BUYER(S):', signature lines, or 'Purchaser'",
  "seller": "Full legal name(s) - look for 'SELLER:', 'OWNER OF RECORD', signature lines",
  "address": "Property address being sold (NOT personal addresses)",
  "purchase_price": "Total purchase amount - format as $XXX,XXX",
  "contract_date": "MM/DD/YYYY - Date contract was signed/executed",
  "deposit_1": "First deposit - format: '$X,XXX due MM/DD/YYYY' or '$X,XXX completed'",
  "deposit_2": "Second deposit (if exists) - same format, or null",
  "deposit_3": "Third deposit (if exists) - same format, or null",
  "closing_date": "MM/DD/YYYY - When title transfers",
  "inspection_deadline": "MM/DD/YYYY - General home/property inspection deadline, or null if waived or not stated",
  "water_inspection_deadline": "ONLY if contract explicitly states a DIFFERENT deadline for water inspection than general inspection, otherwise null",
  "pest_inspection_deadline": "ONLY if contract explicitly states a DIFFERENT deadline for pest inspection than general inspection, otherwise null",
  "septic_inspection_deadline": "ONLY if contract explicitly states a DIFFERENT deadline for septic inspection than general inspection, otherwise null",
  "radon_inspection_deadline": "ONLY if contract explicitly states a DIFFERENT deadline for radon inspection than general inspection, otherwise null",
  "lead_inspection_deadline": "ONLY if contract explicitly states a DIFFERENT deadline for lead inspection than general inspection, otherwise null",
  "environmental_inspection_deadline": "ONLY if contract explicitly states a DIFFERENT deadline for environmental inspection than general inspection, otherwise null",
  "structural_inspection_deadline": "ONLY if contract explicitly states a DIFFERENT deadline for structural inspection than general inspection, otherwise null",
  "hvac_inspection_deadline": "ONLY if contract explicitly states a DIFFERENT deadline for HVAC inspection than general inspection, otherwise null",
  "financing_deadline": "MM/DD/YYYY - Loan approval deadline, or null if waived or not stated",
  "attorney_review_deadline": "MM/DD/YYYY - Attorney review period, or null if not stated",
  "other_important_terms": [
    "List only UNUSUAL or ATYPICAL terms that are explicitly in the contract",
    "Skip standard boilerplate",
    "Include: unusual seller credits, special contingencies, rent-back arrangements, seller financing, property restrictions",
    "For documents with addenda: add 'See attached addendum(s)' and extract only actionable terms with dollar amounts or special contingencies"
  ]
}

DEADLINE EXTRACTION RULES (Critical for accurate parsing):

When parsing contracts for dates and deadlines, use this decision framework:

EXTRACT AS DEADLINE if the language indicates a FUTURE OBLIGATION:
✓ "Buyer shall [action] by [date/timeframe]"
✓ "Inspection must be completed by"
✓ "Deadline: [date]"
✓ "[Action] to be performed within X days"
✓ "Buyer has X days to [action]" (when tied to a reference date like offer acceptance)
✓ Future tense or mandatory language
✓ Contingency language with specific performance dates

Examples that ARE deadlines:
- "Buyer shall have 10 days to conduct home inspection" → EXTRACT (future obligation)
- "Inspection deadline: October 30, 2025" → EXTRACT (specific future date)
- "Appraisal contingency: 15 days from offer acceptance" → EXTRACT (calculable deadline)

DO NOT EXTRACT if the language indicates PAST ACKNOWLEDGEMENT or WAIVER:
✗ Contains words like: "acknowledges," "has been given," "was allowed," "has allowed"
✗ Past tense verbs indicating completed opportunities
✗ Waiver language: "waives," "releases," "disclaims"
✗ Disclosure/notification language without a performance requirement
✗ "Buyer acknowledges having been informed"
✗ "Seller has provided X days"

Examples that are NOT deadlines:
- "Buyer acknowledges being given 10 days to inspect" → IGNORE (past acknowledgement)
- "Seller has allowed buyer 10 days" → IGNORE (past tense)
- "Buyer waives inspection contingency" → IGNORE (waiver)
- "Buyer acknowledges receipt of lead paint disclosure" → IGNORE (disclosure)

KEY DISTINCTION:
- Future obligation with performance required = DEADLINE
- Past acknowledgement of opportunity given = WAIVER

When in doubt: If there's no specific future date or calculable deadline tied to a reference date, it's likely a waiver.

Important rules:
- BUYER = person PURCHASING the property (paying money, getting ownership)
- SELLER = person SELLING the property (current owner, receiving money)
- Property address is the SUBJECT PROPERTY, not personal addresses
- DO NOT create or infer dates that are not explicitly written in the contract
- DO NOT duplicate the same date across multiple inspection fields
- Most contracts have ONE general inspection deadline - use that for "inspection_deadline" ONLY
- ONLY fill specific inspection deadlines (water, pest, etc.) if the contract explicitly states different dates for them
- If the contract says "inspection deadline" without specifying types, leave all specific inspection fields as null
- Use null for ANY field not found in the contract
- Standardize all dates to MM/DD/YYYY format
- Return ONLY the JSON object, no markdown formatting

VERIFICATION CHECKLIST (complete BEFORE returning JSON):

1. BUYER/SELLER ROLE VERIFICATION:
   - Read the contract carefully to determine who is acquiring vs. who is conveying the property
   - BUYER = the party PURCHASING/ACQUIRING the property (they are paying money and getting ownership)
   - SELLER = the party SELLING/CONVEYING the property (they currently own it and are receiving money)
   - Look for context clues: "buyer's financing contingency", "seller's disclosure", "seller to provide", "buyer to deposit"
   - If the contract mentions "buyer shall obtain financing" - the buyer is the one getting the loan, NOT providing seller financing
   - If you see "seller disclosure" or "seller shall convey" - that's the current owner
   - Double-check: Does it make logical sense? If person A is getting a mortgage, they are the BUYER
   - If you accidentally swapped them, CORRECT IT before returning the JSON

2. CONTRACT DATE VERIFICATION:
   - Contract date = the date when the contract became FULLY EXECUTED (binding on all parties)
   - This is typically the LATEST signature date (the date when the last required party signed)
   - DO NOT use: deed recording dates, property recording dates, historical dates, or dates from property records
   - Look for: "Date of Acceptance", "Effective Date", signature timestamps, or the most recent signature date
   - If there are multiple signature dates (buyer signed 1/15, seller signed 1/18), use the LATEST one (1/18)
   - If only one signature date is visible, use that date
   - The contract date should be BEFORE or EQUAL to the closing date (it can't be after closing)
   - Double-check: Does this date make logical sense as when the contract was executed?

3. PROPERTY ADDRESS VERIFICATION:
   - Ensure the address is the SUBJECT PROPERTY (the real estate being bought/sold)
   - NOT the buyer's mailing address, NOT the seller's residence if they live elsewhere
   - Look for: "Property Address:", "Premises:", "Subject Property:", or property description section
   - The address should match what's being conveyed in the deed/purchase agreement

4. FINAL LOGIC CHECK:
   - Do all the dates make chronological sense? (contract date < inspection deadline < closing date)
   - Are buyer and seller correctly identified based on who is purchasing vs. selling?
   - Is the property address the actual property being sold?
   - Did you avoid creating dates that aren't explicitly in the contract?

After completing this verification, return your CORRECTED JSON.

CONTRACT DOCUMENT:
${contractText}
`;

    console.log("🤖 Sending to Gemini for processing...");
    const responseText = await sendToGemini(prompt);
    console.log("🤖 Raw Gemini response:", responseText);

    // Clean up response and parse JSON
    const cleanedText = responseText
      .replace(/```json|```/g, "")
      .replace(/^\s*```\s*|\s*```\s*$/g, "")
      .trim();
    
    console.log("🧹 Cleaned Gemini response:", cleanedText);

    try {
  const data = JSON.parse(cleanedText);
  console.log("✅ Successfully parsed contract data:", data);

  // 🆕 NEW: Validate with ContractValidator
  console.log("🔍 Running ContractValidator...");
  const validator = new ContractValidator(data, contractText);
  const validationResult = validator.validate();
  
  console.log("✅ Validation complete");
  console.log(`📊 Confidence: ${validationResult.confidence}%`);
  console.log(`⚠️ Warnings: ${validationResult.warnings.length}`);
  console.log(`❌ Errors: ${validationResult.errors.length}`);
  
  // Log warnings for debugging
  if (validationResult.warnings.length > 0) {
    console.log("⚠️ WARNINGS:", validationResult.warnings);
  }
  
  // Log errors for debugging
  if (validationResult.errors.length > 0) {
    console.log("❌ ERRORS:", validationResult.errors);
  }
  
  // Return success object with validated data
  return {
    success: true,
    data: validationResult.data,  // ← Using VALIDATED data now
    validation: {
      errors: validationResult.errors,
      warnings: validationResult.warnings,
      confidence: validationResult.confidence
    },
    extractedTextLength: contractText.length,
    processingTime: Date.now()
  };
      
    } catch (parseError) {
      console.error("❌ JSON Parse Error:", parseError);
      console.error("❌ Problematic text:", cleanedText);
      
      // Try to extract any JSON from the response
      const jsonMatch = cleanedText.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        try {
          const fallbackData = JSON.parse(jsonMatch[0]);
          console.log("✅ Fallback parsing successful:", fallbackData);
          
          // Apply boilerplate filtering and addenda processing to fallback data as well
          if (fallbackData.other_important_terms && Array.isArray(fallbackData.other_important_terms)) {
            const originalCount = fallbackData.other_important_terms.length;
            
            // First apply boilerplate filtering
            fallbackData.other_important_terms = fallbackData.other_important_terms.filter(isCustomTerm);
            const boilerplateFilteredCount = fallbackData.other_important_terms.length;
            
            console.log(`🔍 Filtered boilerplate terms (fallback): ${originalCount} → ${boilerplateFilteredCount} terms`);
            
            // Then apply intelligent addenda processing
            fallbackData.other_important_terms = processAddendaTerms(fallbackData.other_important_terms, contractText);
            const finalCount = fallbackData.other_important_terms.length;
            
            console.log(`📄 Final term processing (fallback): ${boilerplateFilteredCount} → ${finalCount} terms`);
            
            if (fallbackData.other_important_terms.length === 0) {
              fallbackData.other_important_terms = [];
            }
          }
          
          return {
            success: true,
            data: fallbackData,
            extractedTextLength: contractText.length,
            warning: "Had to use fallback JSON parsing"
          };
        } catch (fallbackError) {
          console.error("❌ Fallback parsing also failed:", fallbackError);
        }
      }
      
      // Enhanced error response with more details
      return {
        success: false,
        error: "Failed to parse contract data from AI response",
        details: {
          parseError: parseError.message,
          responseLength: cleanedText.length,
          containsJson: cleanedText.includes('{') && cleanedText.includes('}'),
          sampleResponse: cleanedText.substring(0, 200)
        },
        rawResponse: cleanedText.substring(0, 500),
        extractedTextLength: contractText.length,
        suggestions: [
          "Try uploading the contract again",
          "Ensure the PDF contains readable text (not just scanned images)",
          "Check if the contract is in a standard format"
        ]
      };
    }

  } catch (error) {
    console.error("❌ Contract processing error:", error);
    return {
      success: false,
      error: error.message,
      stack: error.stack
    };
  }
}
